import type { QueryClient } from '@tanstack/react-query';

import { logger } from '@/lib/logger';

import { associationTypesQueryKey } from '../hooks/useAssociationTypes';
import { entityTypesQueryKey } from '../hooks/useEntityTypeSchema';

/**
 * Invalidates the CANONICAL CRM schema React Query caches — the ones nav and
 * list pages actually read — after an object-type create/rename/delete.
 *
 * Always go through the exported key factories (`entityTypesQueryKey`,
 * `associationTypesQueryKey`), never re-typed literal keys: key drift between
 * a mutation's invalidation and a reader's query key is the root cause of
 * #14179 (a renamed/created custom object showing "not found" until a hard
 * refresh, because the cache the nav/list actually read was never
 * invalidated).
 *
 * The legacy `['objectTypes', orgId]` key is also invalidated (with
 * `exact: false` to catch its derived variants) because it is still read
 * live by several call sites — e.g. `InlineAssociationEditor`, the
 * create-object-type wizard's type-name collision check, and derived
 * resolveSlug/customEntities queries. Dropping it would regress those.
 *
 * Non-throwing by contract: each `invalidateQueries` passes
 * `{ throwOnError: true }` so a failed active refetch rejects its promise
 * (by default React Query swallows refetch errors and resolves, which would
 * make the `logger.error` path below dead code). `Promise.allSettled` then
 * captures those rejections and logs them without ever rejecting itself, so
 * callers can safely `await` this before navigating — a failed refetch is
 * logged but never blocks navigation.
 */
export async function invalidateSchemaCaches(
  queryClient: QueryClient,
  orgId: number
): Promise<void> {
  const results = await Promise.allSettled([
    queryClient.invalidateQueries(
      { queryKey: entityTypesQueryKey(orgId) },
      { throwOnError: true }
    ),
    queryClient.invalidateQueries(
      { queryKey: associationTypesQueryKey(orgId) },
      { throwOnError: true }
    ),
    queryClient.invalidateQueries(
      { queryKey: ['objectTypes', orgId], exact: false },
      { throwOnError: true }
    ),
  ]);

  for (const result of results) {
    if (result.status === 'rejected') {
      logger.error(
        'invalidateSchemaCaches: schema cache refetch failed',
        result.reason instanceof Error
          ? result.reason
          : new Error(String(result.reason)),
        { org_id: orgId }
      );
    }
  }
}
